import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from './authHelpers';

const useFantasyTeamStats = (userNumber, shouldFetch = false) => {
  const [stats, setStats] = useState({
    highestPointsTeam: null,
    mostUsedCard: null,
    highestPointsCard: null
  });
  const [loading, setLoading] = useState({
    highestPointsTeam: false,
    mostUsedCard: false,
    highestPointsCard: false
  });
  const [error, setError] = useState({
    highestPointsTeam: null,
    mostUsedCard: null,
    highestPointsCard: null
  });

  const fetchHighestPointsTeam = useCallback(async (userNumber) => {
    if (!userNumber) return null;
    
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/user/${userNumber}/highest-points`, 'GET');
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No fantasy teams found
        }
        throw new Error('Failed to fetch highest points team');
      }
      const data = await response.json();
      return data.fantasyTeam;
    } catch (error) {
      console.error('Error fetching highest points team');
      throw error;
    }
  }, []);

  const fetchMostUsedCard = useCallback(async (userNumber) => {
    if (!userNumber) return null;
    
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/user/${userNumber}/card/most-used`, 'GET');
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No cards found
        }
        throw new Error('Failed to fetch most used card');
      }
      const data = await response.json();
      return data.cardStats;
    } catch (error) {
      console.error('Error fetching most used card');
      throw error;
    }
  }, []);

  const fetchHighestPointsCard = useCallback(async (userNumber) => {
    if (!userNumber) return null;
    
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/user/${userNumber}/card/highest-points`, 'GET');
      if (!response.ok) {
        if (response.status === 404) {
          return null; // No cards found for this user
        }
        throw new Error('Failed to fetch highest points card');
      }
      const data = await response.json();
      return data.cardRecord;
    } catch (error) {
      console.error('Error fetching highest points card');
      throw error;
    }
  }, []);

  const fetchAllStats = useCallback(async () => {
    if (!shouldFetch || !userNumber) return;

    // Set individual loading states to true
    setLoading({
      highestPointsTeam: true,
      mostUsedCard: true,
      highestPointsCard: true
    });

    // Clear previous errors
    setError({
      highestPointsTeam: null,
      mostUsedCard: null,
      highestPointsCard: null
    });

    try {
      // Fetch highest points team
      const highestPointsTeamPromise = fetchHighestPointsTeam(userNumber)
        .then(result => {
          setStats(prev => ({ ...prev, highestPointsTeam: result }));
          setLoading(prev => ({ ...prev, highestPointsTeam: false }));
          return result;
        })
        .catch(error => {
          setError(prev => ({ ...prev, highestPointsTeam: error.message }));
          setLoading(prev => ({ ...prev, highestPointsTeam: false }));
          throw error;
        });

      // Fetch most used card
      const mostUsedCardPromise = fetchMostUsedCard(userNumber)
        .then(result => {
          setStats(prev => ({ ...prev, mostUsedCard: result }));
          setLoading(prev => ({ ...prev, mostUsedCard: false }));
          return result;
        })
        .catch(error => {
          setError(prev => ({ ...prev, mostUsedCard: error.message }));
          setLoading(prev => ({ ...prev, mostUsedCard: false }));
          throw error;
        });

      // Fetch highest points card
      const highestPointsCardPromise = fetchHighestPointsCard(userNumber)
        .then(result => {
          setStats(prev => ({ ...prev, highestPointsCard: result }));
          setLoading(prev => ({ ...prev, highestPointsCard: false }));
          return result;
        })
        .catch(error => {
          setError(prev => ({ ...prev, highestPointsCard: error.message }));
          setLoading(prev => ({ ...prev, highestPointsCard: false }));
          throw error;
        });

      // Wait for all promises to complete (but don't fail if one fails)
      await Promise.allSettled([
        highestPointsTeamPromise,
        mostUsedCardPromise,
        highestPointsCardPromise
      ]);

    } catch (error) {
      console.error('Error fetching fantasy team stats');
      // Individual errors are already handled above
    }
  }, [userNumber, shouldFetch, fetchHighestPointsTeam, fetchMostUsedCard, fetchHighestPointsCard]);

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchAllStats
  };
};

export { useFantasyTeamStats };
